import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBlocker } from 'react-router-dom'
import axios from 'axios'
import { Loader2, Save, Calculator, Euro, Percent, TrendingUp, AlertCircle, Check, Users, Truck, Crown } from 'lucide-react'
import { useToast } from '@/components/ui/Toast'

interface PricingTier {
    min: number
    max: number
    margin_percent: number
}

interface PricingConfig {
    vat_rate: number
    tiers_pvp: PricingTier[]
    tiers_pvd: PricingTier[]
    tiers_vip: PricingTier[]
}

interface PriceResult {
    cost_price: number
    tariff: string
    margin_percent: number
    net_price_theoretical: number
    price_with_vat_theoretical: number
    price_with_vat_rounded: number
    final_net_price: number
}

interface MultiPriceResult {
    pvp: PriceResult
    pvd: PriceResult
    vip: PriceResult
}

type TariffType = 'pvp' | 'pvd' | 'vip'

export default function PricingConfigPage() {
    const toast = useToast()
    const queryClient = useQueryClient()
    const [config, setConfig] = useState<PricingConfig | null>(null)
    const [activeTab, setActiveTab] = useState<TariffType>('pvp')
    const [testCost, setTestCost] = useState<string>('')
    const [testResult, setTestResult] = useState<MultiPriceResult | null>(null)
    const [isEditingVat, setIsEditingVat] = useState(false)
    const [isDirty, setIsDirty] = useState(false)

    // Block navigation if dirty
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty && currentLocation.pathname !== nextLocation.pathname
    )

    useEffect(() => {
        if (blocker.state === "blocked") {
            const confirm = window.confirm("Tienes cambios sin guardar. ¿Seguro que quieres salir? Perderás los cambios.")
            if (confirm) {
                blocker.proceed()
            } else {
                blocker.reset()
            }
        }
    }, [blocker])

    // Warn on unsaved changes when leaving page (browser refresh/close)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [isDirty])

    // Fetch Config
    const { data, isLoading } = useQuery({
        queryKey: ['pricingConfig'],
        queryFn: async () => {
            const res = await axios.get('/api/pricing/config')
            return res.data as PricingConfig
        }
    })

    // Update local state when data loads
    useEffect(() => {
        if (data) setConfig(data)
    }, [data])

    // Save Mutation
    const saveMutation = useMutation({
        mutationFn: async (newConfig: PricingConfig) => {
            await axios.post('/api/pricing/config', newConfig)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pricingConfig'] })
            setIsDirty(false)
            toast.success('Configuracion guardada correctamente')
        },
        onError: (err) => {
            console.error(err)
            toast.error('Error al guardar configuracion')
        }
    })

    // Calculate Mutation
    const calculateMutation = useMutation({
        mutationFn: async (cost: number) => {
            const res = await axios.post('/api/pricing/calculate', { cost_price: cost })
            return res.data as MultiPriceResult
        },
        onSuccess: (data) => {
            setTestResult(data)
        }
    })

    const handleTierChange = (index: number, field: keyof PricingTier, value: string) => {
        if (!config) return

        const tierKey = `tiers_${activeTab}` as keyof PricingConfig
        const currentTiers = config[tierKey] as PricingTier[]
        const newTiers = [...currentTiers]

        newTiers[index] = { ...newTiers[index], [field]: parseFloat(value) || 0 }

        setConfig({ ...config, [tierKey]: newTiers })
        setIsDirty(true)
    }

    const addTier = () => {
        if (!config) return
        const tierKey = `tiers_${activeTab}` as keyof PricingConfig
        const currentTiers = config[tierKey] as PricingTier[]

        // Find logical next min value
        const lastMax = currentTiers.length > 0 ? currentTiers[currentTiers.length - 1].max : 0

        const newTier: PricingTier = { min: lastMax, max: lastMax + 100, margin_percent: 20 }
        setConfig({ ...config, [tierKey]: [...currentTiers, newTier] })
        setIsDirty(true)
    }

    const removeTier = (index: number) => {
        if (!config) return
        const tierKey = `tiers_${activeTab}` as keyof PricingConfig
        const currentTiers = config[tierKey] as PricingTier[]
        const newTiers = currentTiers.filter((_, i) => i !== index)
        setConfig({ ...config, [tierKey]: newTiers })
        setIsDirty(true)
    }

    const handleTabChange = (tab: TariffType) => {
        if (activeTab === tab) return
        if (isDirty) {
            if (!window.confirm('Tienes cambios sin guardar. ¿Seguro que quieres cambiar de pestaña? Perderás los cambios no guardados si recargas, pero se mantienen en memoria mientras sigas en la página.')) {
                return
            }
        }
        setActiveTab(tab)
    }

    const handleVatChange = (value: string) => {
        if (!config) return
        setConfig({ ...config, vat_rate: parseFloat(value) || 0 })
        setIsDirty(true)
    }

    const handleSave = () => {
        if (config) saveMutation.mutate(config)
    }

    const handleVatSave = () => {
        setIsEditingVat(false)
        handleSave()
    }

    const handleTest = (e: React.FormEvent) => {
        e.preventDefault()
        if (testCost) calculateMutation.mutate(parseFloat(testCost))
    }

    if (isLoading || !config) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
                    <p className="text-slate-500 font-medium">Cargando configuración...</p>
                </div>
            </div>
        )
    }

    const activeTiers = config[`tiers_${activeTab}`] || []

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                        <span className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <Euro className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        </span>
                        Configuración de Precios
                    </h1>
                    <p className="mt-2 text-slate-600 dark:text-slate-400">
                        Define tus reglas de márgenes por tarifa y simula precios finales.
                    </p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                    className={`
                        flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all
                        ${saveMutation.isPending
                            ? 'bg-gray-400 cursor-not-allowed'
                            : saveMutation.isSuccess
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-primary-600 hover:bg-primary-700 hover:scale-105'
                        }
                    `}
                >
                    {saveMutation.isPending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : saveMutation.isSuccess ? (
                        <Check className="w-5 h-5" />
                    ) : (
                        <Save className="w-5 h-5" />
                    )}
                    {saveMutation.isSuccess ? 'Guardado!' : 'Guardar Cambios'}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Configuration Column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Global Settings Card */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary-500" />
                            Ajustes Generales
                        </h2>
                        <div className="flex items-center gap-4">
                            <div className="w-full md:w-1/3">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    IVA General (%)
                                </label>
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <input
                                            type="number"
                                            value={config.vat_rate}
                                            onChange={(e) => handleVatChange(e.target.value)}
                                            disabled={!isEditingVat}
                                            className={`
                                                w-full pl-4 pr-10 py-2 rounded-lg border transition-all
                                                text-slate-900 dark:text-white
                                                ${isEditingVat
                                                    ? 'bg-white dark:bg-slate-900 border-blue-500 ring-2 ring-blue-500/20'
                                                    : 'bg-slate-100 dark:bg-slate-800 border-transparent cursor-not-allowed font-semibold'
                                                }
                                            `}
                                        />
                                        <Percent className="absolute right-3 top-2.5 w-4 h-4 text-slate-400" />
                                    </div>

                                    {isEditingVat ? (
                                        <button
                                            onClick={handleVatSave}
                                            className="p-2 bg-success-100 dark:bg-green-900/30 text-success-600 dark:text-success-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                                            title="Guardar IVA"
                                        >
                                            <Check className="w-5 h-5" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setIsEditingVat(true)}
                                            className="p-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                                            title="Editar IVA"
                                        >
                                            <div className="w-5 h-5 flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                                <p className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 shrink-0" />
                                    El IVA se aplica sobre el precio neto calculado (Coste + Margen) para obtener el PVP final.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Tiers Card with Tabs */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                            <div className="p-6 pb-0">
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
                                    <TrendingUp className="w-5 h-5 text-success-500" />
                                    Márgenes por Tarifa
                                </h2>

                                {/* Tabs */}
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleTabChange('pvp')}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors border-b-2
                                            ${activeTab === 'pvp'
                                                ? 'bg-white dark:bg-slate-800 text-primary-600 border-blue-600'
                                                : 'text-slate-500 hover:text-gray-700 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700'
                                            }
                                        `}
                                    >
                                        <Users className="w-4 h-4" /> PVP (Público)
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('pvd')}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors border-b-2
                                            ${activeTab === 'pvd'
                                                ? 'bg-white dark:bg-slate-800 text-purple-600 border-purple-600'
                                                : 'text-slate-500 hover:text-gray-700 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700'
                                            }
                                        `}
                                    >
                                        <Truck className="w-4 h-4" /> PVD (Distribuidor)
                                    </button>
                                    <button
                                        onClick={() => handleTabChange('vip')}
                                        className={`
                                            flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-colors border-b-2
                                            ${activeTab === 'vip'
                                                ? 'bg-white dark:bg-slate-800 text-amber-600 border-amber-600'
                                                : 'text-slate-500 hover:text-gray-700 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700'
                                            }
                                        `}
                                    >
                                        <Crown className="w-4 h-4" /> VIP
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-12 gap-4 mb-4 px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <div className="col-span-4">Coste Mínimo (€)</div>
                                <div className="col-span-4">Coste Máximo (€)</div>
                                <div className="col-span-3">Margen (%)</div>
                                <div className="col-span-1"></div>
                            </div>

                            <div className="space-y-3">
                                {activeTiers.map((tier, index) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-12 gap-4 items-center p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600"
                                    >
                                        <div className="col-span-4 relative">
                                            <input
                                                type="number"
                                                value={tier.min}
                                                onChange={(e) => handleTierChange(index, 'min', e.target.value)}
                                                className="w-full pl-8 py-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                            <span className="absolute left-3 top-2.5 text-slate-400">€</span>
                                        </div>
                                        <div className="col-span-4 relative">
                                            <input
                                                type="number"
                                                value={tier.max}
                                                onChange={(e) => handleTierChange(index, 'max', e.target.value)}
                                                className="w-full pl-8 py-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                            <span className="absolute left-3 top-2.5 text-slate-400">€</span>
                                        </div>
                                        <div className="col-span-3 relative">
                                            <input
                                                type="number"
                                                value={tier.margin_percent}
                                                onChange={(e) => handleTierChange(index, 'margin_percent', e.target.value)}
                                                className="w-full pl-4 pr-8 py-2 font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-blue-200 dark:border-blue-800 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            />
                                            <Percent className="absolute right-3 top-2.5 w-4 h-4 text-blue-400" />
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            <button
                                                onClick={() => removeTier(index)}
                                                className="p-2 text-red-400 hover:text-danger-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={addTier}
                                className="mt-4 w-full py-2 border-2 border-dashed border-slate-300 dark:border-gray-700 rounded-xl text-slate-500 hover:text-primary-600 hover:border-blue-300 dark:hover:border-blue-800 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 12h8" /><path d="M12 8v8" /></svg>
                                Añadir Nuevo Tramo
                            </button>
                        </div>
                    </div>
                </div>

                {/* Simulator Column */}
                <div className="lg:col-span-1">
                    <div className="sticky top-6 space-y-6">
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                                <Calculator className="w-6 h-6 text-blue-400" />
                                Simulador de Precio
                            </h2>

                            <form onSubmit={handleTest} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2">
                                        Precio de Coste
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={testCost}
                                            onChange={(e) => setTestCost(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-gray-400 text-lg font-medium"
                                            placeholder="0.00"
                                        />
                                        <span className="absolute left-4 top-3.5 text-slate-400">€</span>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full py-3 bg-primary-600 hover:bg-blue-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-900/30 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <Calculator className="w-5 h-5" />
                                    Calcular Precios
                                </button>
                            </form>

                            {testResult && testResult.pvp && (
                                <div className="mt-8 pt-8 border-t border-gray-700 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                    {/* PVP Result */}
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-blue-300 font-semibold flex items-center gap-2">
                                                <Users className="w-4 h-4" /> PVP
                                            </span>
                                            <span className="text-xs bg-blue-900/50 text-blue-200 px-2 py-0.5 rounded">
                                                Margen: {testResult.pvp.margin_percent}%
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 border-t border-blue-500/20 pt-3">
                                            <div>
                                                <span className="block text-xs text-slate-400 mb-1">Precio Neto</span>
                                                <span className="text-lg font-mono text-gray-200">
                                                    {testResult.pvp.net_price_theoretical.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    Sin IVA · Teórico (sin redondeo)
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="block text-xs text-slate-400 mb-1">PVP</span>
                                                <span className="text-xl font-bold text-white">
                                                    {testResult.pvp.price_with_vat_rounded.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    IVA incl. · Redondeado a ,99
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* PVD Result */}
                                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-purple-300 font-semibold flex items-center gap-2">
                                                <Truck className="w-4 h-4" /> PVD
                                            </span>
                                            <span className="text-xs bg-purple-900/50 text-purple-200 px-2 py-0.5 rounded">
                                                Margen: {testResult.pvd.margin_percent}%
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 border-t border-purple-500/20 pt-3">
                                            <div>
                                                <span className="block text-xs text-slate-400 mb-1">Precio Neto</span>
                                                <span className="text-lg font-mono text-gray-200">
                                                    {testResult.pvd.net_price_theoretical.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    Sin IVA · Sin redondeo
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="block text-xs text-slate-400 mb-1">PVD</span>
                                                <span className="text-xl font-bold text-white">
                                                    {testResult.pvd.price_with_vat_rounded.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    IVA incl. · Sin redondeo
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* VIP Result */}
                                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <span className="text-amber-300 font-semibold flex items-center gap-2">
                                                <Crown className="w-4 h-4" /> VIP
                                            </span>
                                            <span className="text-xs bg-amber-900/50 text-amber-200 px-2 py-0.5 rounded">
                                                Margen: {testResult.vip.margin_percent}%
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 border-t border-amber-500/20 pt-3">
                                            <div>
                                                <span className="block text-xs text-slate-400 mb-1">Precio Neto</span>
                                                <span className="text-lg font-mono text-gray-200">
                                                    {testResult.vip.net_price_theoretical.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    Sin IVA · Sin redondeo
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="block text-xs text-slate-400 mb-1">VIP</span>
                                                <span className="text-xl font-bold text-white">
                                                    {testResult.vip.price_with_vat_rounded.toFixed(2)}€
                                                </span>
                                                <span className="block text-[10px] text-slate-500 mt-1">
                                                    IVA incl. · Sin redondeo
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
